import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon, PlayArrow as PlayIcon } from '@mui/icons-material';

/** A YouTube video in a post: its thumbnail first, the player (privacy-enhanced, no cookies until played)
 *  only once clicked, so a feed full of videos stays light. */
export default function YouTubeEmbed({ video, title, onDismiss }) {
  const [playing, setPlaying] = useState(false);
  if (!video?.id) return null;
  const params = new URLSearchParams({ autoplay: '1', rel: '0', ...(video.start ? { start: String(video.start) } : {}) });
  return (
    <Box onClick={(e) => e.stopPropagation()}
      sx={{ mt: 2, mb: 2, position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 1, overflow: 'hidden', bgcolor: '#000' }}>
      {playing ? (
        <Box component="iframe" src={`https://www.youtube-nocookie.com/embed/${video.id}?${params}`}
          title={title || 'YouTube video'} allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
      ) : (
        <Box component="button" type="button" onClick={() => setPlaying(true)} aria-label={`Play ${title || 'the video'}`}
          sx={{ position: 'absolute', inset: 0, p: 0, border: 0, cursor: 'pointer', bgcolor: 'transparent',
            '&:hover .play, &:focus-visible .play': { bgcolor: '#ff0000' } }}>
          {/* an <img> (not a CSS background) so the thumbnail loads only when scrolled into view */}
          <Box component="img" src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`} alt="" loading="lazy" decoding="async"
            referrerPolicy="no-referrer" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <Box className="play" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 68, height: 48, borderRadius: 3, bgcolor: 'rgba(23,23,23,0.8)', display: 'grid', placeItems: 'center', transition: 'background 0.15s' }}>
            <PlayIcon sx={{ color: '#fff', fontSize: 34 }} />
          </Box>
          {title && (
            <Typography noWrap sx={{ position: 'absolute', left: 0, right: 0, top: 0, pl: 1.5, pr: onDismiss ? 5 : 1.5, py: 1, color: '#fff', fontSize: 14, fontWeight: 600,
              textAlign: 'left', background: 'linear-gradient(rgba(0,0,0,0.6), transparent)' }}>
              {title}
            </Typography>
          )}
        </Box>
      )}
      {onDismiss && (
        <Tooltip title="Remove the video preview">
          <IconButton size="small" aria-label="Remove the video preview" onClick={onDismiss}
            sx={{ position: 'absolute', top: 6, right: 6, color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
